export class ThirdPartyLoaderService {
    // Maintain a cache of promises to avoid loading the same script multiple times
    private static scriptPromises: Record<string, Promise<void>> = {};

    static loadScript({
        src,
        isModule,
        async = true,
        defer = true,
        crossOrigin
    }: {
        isModule: boolean;
    } & Pick<HTMLScriptElement, 'src'> &
        Pick<Partial<HTMLScriptElement>, 'async' | 'defer' | 'crossOrigin'>) {
        // If we're already loading (or have loaded) this script, return the cached promise
        if (ThirdPartyLoaderService.scriptPromises[src]) {
            return ThirdPartyLoaderService.scriptPromises[src];
        }

        // Create a new promise for this src and cache it
        const loadPromise = new Promise<void>((resolve, reject) => {
            const existingScript = document.querySelector(
                `script[src='${src}']`
            );

            // If the script tag already exists in the document, resolve immediately
            if (existingScript) {
                return resolve();
            }

            const newScript = document.createElement('script');
            newScript.type = isModule ? 'module' : 'text/javascript';
            newScript.async = async;
            newScript.defer = defer;

            if (crossOrigin) {
                newScript.crossOrigin = crossOrigin;
            }

            newScript.src = src;

            newScript.addEventListener(
                'load',
                () => {
                    resolve();
                },
                { once: true }
            );

            newScript.addEventListener(
                'error',
                error => {
                    // Remove the cached promise so subsequent attempts can retry
                    delete ThirdPartyLoaderService.scriptPromises[src];
                    reject(error);
                },
                { once: true }
            );

            document.head.appendChild(newScript);
        });

        ThirdPartyLoaderService.scriptPromises[src] = loadPromise;

        return loadPromise;
    }

    static loadCss(href: string) {
        return new Promise<void>((resolve, reject) => {
            const existingLink = document.querySelector(`link[href='${href}']`);
            if (existingLink) {
                return resolve();
            }
            const newLink = document.createElement('link');
            document.head.appendChild(newLink);
            newLink.onload = () => resolve();
            newLink.onerror = reject;
            newLink.type = 'text/css';
            newLink.rel = 'stylesheet';
            newLink.href = href;
        });
    }
}
